import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import BookBuyLink from "./BookBuyLink";

/**
 * Reusable book card used by both the Finance series and the AI series.
 * Alternates cover/details left-right based on index.
 */
export default function BookCard({
  book,
  index,
  totalInSeries = 3,
  country,
  countryStatus,
}) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [40, -40]);

  const align = index % 2 === 0 ? "left" : "right";

  return (
    <motion.article
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 1, ease: [0.2, 0.8, 0.2, 1] }}
      className="grid grid-cols-12 gap-6 md:gap-12 items-center"
      data-testid={`book-card-${book.id}`}
    >
      {/* Cover */}
      <div
        className={`col-span-12 md:col-span-6 ${
          align === "right" ? "md:order-2" : ""
        }`}
      >
        <div
          className="spotlight tilt-card aspect-square flex items-center justify-center relative"
          style={{
            background: `radial-gradient(120% 80% at 50% 20%, ${book.color}22, transparent 60%), var(--paper)`,
          }}
        >
          <motion.img
            src={book.cover}
            alt={book.title}
            style={{ y }}
            className="w-[72%] shadow-[0_30px_60px_rgba(28,25,23,0.18)] rounded-[6px]"
            data-testid={`book-cover-${book.id}`}
          />
          <span className="folio-num absolute top-6 left-6">Book № {book.number}</span>
          <span className="folio-num absolute bottom-6 right-6">{book.year}</span>
        </div>
      </div>

      {/* Details */}
      <div className={`col-span-12 md:col-span-6 ${align === "right" ? "md:order-1" : ""}`}>
        <span className="folio-num">
          {book.number} / {String(totalInSeries).padStart(2, "0")} · {book.status.toUpperCase()}
        </span>
        <h3 className="mt-4 font-serif text-4xl md:text-6xl leading-[0.95] tracking-tight">
          {book.title}
        </h3>
        <p className="mt-3 font-serif italic text-xl md:text-2xl text-stone-600">
          {book.subtitle}
        </p>

        <p className="mt-6 text-[16px] md:text-[17px] leading-relaxed text-stone-700 max-w-lg">
          {book.blurb}
        </p>

        <ul className="mt-6 flex flex-wrap gap-2">
          {book.themes.map((t) => (
            <li
              key={t}
              className="text-[11px] uppercase tracking-[0.22em] border border-stone-900/20 px-3 py-1.5"
            >
              {t}
            </li>
          ))}
        </ul>

        <div className="mt-6 flex flex-wrap gap-6 text-[12px] uppercase tracking-[0.25em] text-stone-600">
          <span>Ages {book.ageRange}</span>
          <span>·</span>
          <span>{book.pages} pages</span>
          <span>·</span>
          <span data-testid={`book-format-${book.id}`}>{book.formats || "Paperback"}</span>
        </div>

        <div className="mt-8 flex flex-wrap gap-3 items-center">
          <BookBuyLink book={book} country={country} status={countryStatus} />
          <span
            className="inline-flex items-center gap-2 pl-3 pr-4 py-2 border border-stone-900/25 text-[10px] uppercase tracking-[0.24em]"
            data-testid={`ai-badge-${book.id}`}
          >
            <span
              className="inline-block h-1.5 w-1.5 rounded-full"
              style={{ background: "var(--accent-orange)" }}
            />
            AI-crafted picture book
          </span>
        </div>
      </div>
    </motion.article>
  );
}
